import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { InspectionService } from 'src/app/services/inspections/inspection.service';
import { InspectionModalService } from 'src/app/services/inspection-modal/inspection-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-md-inspection',
  templateUrl: './md-inspection.component.html',
  styleUrls: ['./md-inspection.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class MdInspectionComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  inspections: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedInspections: string[] = [];

  constructor(
    private _inspectionService: InspectionService,
    private _inspectionModalService: InspectionModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadInspections();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadInspections(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedInspections = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._inspectionService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.inspections = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.inspections = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading inspections:', error);
          this.inspections = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadInspections(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadInspections(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadInspections();
  }

  openAddInspectionModal(): void {
    this._inspectionModalService.clearSelectedInspection();
    const modalElement = document.getElementById('kt_modal_inspection_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(inspection: any): void {
    this._inspectionModalService.setSelectedInspection(inspection);
    const modalElement = document.getElementById('kt_modal_inspection_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteInspection(inspection: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${inspection.caption}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDelete(inspection);
      }
    });
  }

  private performDelete(inspection: any): void {
    const inspectionId = inspection._id || inspection.id;
    this.subscriptions.push(
      this._inspectionService.DeleteDataById(inspectionId).subscribe({
        next: (response: any) => {
          if (response.status) {
            Swal.fire({
              title: 'Deleted!',
              text: 'Inspection has been deleted successfully.',
              icon: 'success',
              confirmButtonText: 'OK'
            }).then(() => {
              this.loadInspections(this.currentPage, this.searchTerm, this.filters);
            });
          } else {
            Swal.fire({
              title: 'Error!',
              text: response.message || 'Failed to delete inspection.',
              icon: 'error',
              confirmButtonText: 'OK'
            });
          }
        },
        error: (error: any) => {
          Swal.fire({
            title: 'Error!',
            text: error.error?.message || 'An error occurred while deleting inspection.',
            icon: 'error',
            confirmButtonText: 'OK'
          });
        }
      })
    );
  }

  // Event handlers for modal events
  onInspectionAdded(): void {
    console.log('Inspection added, refreshing list...');
    this.loadInspections(this.currentPage, this.searchTerm, this.filters);
  }

  onInspectionUpdated(): void {
    console.log('Inspection updated, refreshing list...');
    this.loadInspections(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(inspectionId: string): boolean {
    return this.selectedInspections.includes(inspectionId);
  }

  isAllSelected(): boolean {
    return this.inspections.length > 0 && this.selectedInspections.length === this.inspections.length;
  }

  isIndeterminate(): boolean {
    return this.selectedInspections.length > 0 && this.selectedInspections.length < this.inspections.length;
  }

  toggleSelection(inspectionId: string, event: any): void {
    if (event.target.checked) {
      if (!this.selectedInspections.includes(inspectionId)) {
        this.selectedInspections.push(inspectionId);
      }
    } else {
      this.selectedInspections = this.selectedInspections.filter(id => id !== inspectionId);
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedInspections = this.inspections.map(inspection => inspection._id);
    } else {
      this.selectedInspections = [];
    }
    this.toggleToolbars();
  }

  private toggleToolbars(): void {
    const toolbarBase = document.querySelector('[data-kt-inspection-table-toolbar="base"]');
    const toolbarSelected = document.querySelector('[data-kt-inspection-table-toolbar="selected"]');

    if (this.selectedInspections.length > 0) {
      toolbarBase?.classList.add('d-none');
      toolbarSelected?.classList.remove('d-none');
    } else {
      toolbarBase?.classList.remove('d-none');
      toolbarSelected?.classList.add('d-none');
    }
  }

  deleteSelectedInspections(): void {
    if (this.selectedInspections.length === 0) {
      return;
    }

    Swal.fire({
      title: 'Are you sure?',
      text: `You want to delete ${this.selectedInspections.length} selected inspection(s)?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performBulkDelete();
      }
    });
  }

  private performBulkDelete(): void {
    if (this.selectedInspections.length === 0) {
      return;
    }

    this.subscriptions.push(
      this._inspectionService.BulkDelete(this.selectedInspections).subscribe({
        next: () => {
          this.loadInspections();
          this.selectedInspections = [];
          this.toggleToolbars();
          Swal.fire('Deleted!', 'Selected inspections have been deleted.', 'success');
        },
        error: (error) => {
          console.error('Error deleting inspections:', error);
          Swal.fire('Error!', 'Failed to delete selected inspections.', 'error');
        }
      })
    );
  }

  // Math utility for template
  Math = Math;
}
